import React, { useState, useEffect } from "react";
import { Link, useParams } from "react-router-dom";
import { useAuth } from "context/AuthProvider";
import logo from "assets/logo/logo.png";
import Tournament from "Components/Tournament/Tournament";
import LocationDropdown from "Components/Navbar/LocationDropdown";
import {
  fetchArchivedTournamentsByLocation,
  fetchTournamentsByLocation,
  canManage,
} from "services/tournamentService";
import PlayerRankings from "Components/Tournament/PlayerRankings";
import { fetchLocationById } from "services/locationService";
import { fetchMapPointByLocation } from "services/mapPointService";
import TournamentArchive from "Components/Tournament/tournamentArchive";
import { fetchUsers } from "services/userService";
import { getLocationRankings } from "services/rankingService";

import * as Sentry from "@sentry/react";

import TournamentTabBar from "./components/TournamentTabBar";
import LeftSidebar from "./components/LeftSidebar";
import RightSidebar from "./components/RightSidebar";
import PodiumSection from "./components/PodiumSection";
import CollapsibleSection from "./components/CollapsibleSection";
import LoginInvite from "./components/LoginInvite";
import LocationAddress from "./components/LocationAddress";

const Location = () => {
  const { locationId } = useParams();
  const { user, loading: authLoading } = useAuth();
  const [tournaments, setTournaments] = useState([]);
  const [location, setLocation] = useState(null);
  const [users, setUsers] = useState([]);
  const [locationRankings, setLocationRankings] = useState([]);
  const [latestTournamentWithPodium, setLatestTournamentWithPodium] =
    useState(null);
  const [animatePodium, setAnimatePodium] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [selectedTournamentId, setSelectedTournamentId] = useState(null);
  const [addressPoint, setAddressPoint] = useState(null);

  // Address comes from the map point linked to this location (public, so no auth needed)
  useEffect(() => {
    fetchMapPointByLocation(locationId)
      .then(setAddressPoint)
      .catch((error) => {
        Sentry.captureException(error);
        console.error("Error fetching location address:", error);
      });
  }, [locationId]);

  useEffect(() => {
    if (authLoading) return;

    // Logged-out visitors only get the (public) location itself
    if (!user) {
      fetchLocationById(locationId)
        .then(setLocation)
        .catch((error) => {
          Sentry.captureException(error);
          console.error("Error fetching location:", error);
        });
      return;
    }

    const loadData = async () => {
      try {
        const promises = [
          fetchLocationById(locationId),
          fetchTournamentsByLocation(locationId),
          fetchArchivedTournamentsByLocation(locationId),
          getLocationRankings(locationId),
        ];

        if (isAdmin) {
          promises.push(fetchUsers());
        }

        const [
          fetchedLocation,
          fetchedTournaments,
          fetchedArchivedTournaments,
          fetchedRankings,
          fetchedUsers = [],
        ] = await Promise.all(promises);

        setLocation(fetchedLocation);
        setTournaments(fetchedTournaments);
        setUsers(fetchedUsers);
        setLocationRankings(fetchedRankings);

        setIsAdmin(canManage(user, fetchedLocation?.id));

        const tournamentWithPodiums = fetchedArchivedTournaments
          .filter((t) => t.podiums && t.podiums.length > 0)
          .sort((a, b) => new Date(b.date) - new Date(a.date))[0];

        setLatestTournamentWithPodium(tournamentWithPodiums || null);
        setTimeout(() => setAnimatePodium(true), 100);

        // Auto-select first active tournament
        const active = fetchedTournaments
          .filter((t) => t.active === true)
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        if (active.length > 0) {
          setSelectedTournamentId((prev) => {
            // Keep current selection if it's still valid
            if (prev && active.some((t) => t.id === prev)) return prev;
            return active[0].id;
          });
        }
      } catch (error) {
        Sentry.captureException(error);
        console.error("Error fetching data:", error);
      }
    };

    loadData();
  }, [locationId, isAdmin, user, authLoading]);

  const activeTournaments = tournaments
    .filter((t) => t.active === true)
    .sort((a, b) => new Date(a.date) - new Date(b.date));

  const selectedTournament = activeTournaments.find(
    (t) => t.id === selectedTournamentId
  );

  const setLocationImage = (url) => {
    setLocation((prev) => prev ? { ...prev, image: url } : prev);
  };

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col text-slate-50 w-full">
      {/* Navbar — single compact row */}
      <nav className="sticky top-0 z-50 bg-slate-900 shadow-lg">
        <div className="flex items-center justify-between px-4 py-2">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2 text-slate-50">
              <img src={logo} alt="Logo" className="h-8" />
            </Link>
            <span className="text-lg font-semibold italic text-slate-200 hidden sm:inline">
              {location
                ? `${location.name} - ${location.town}`
                : "Chargement ..."}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {user && <LocationDropdown buttonText="Autres hebdos" />}
            {isAdmin && (
              <>
                <Link
                  to={`/locations/tournaments/new/${locationId}`}
                  className="px-3 py-1.5 bg-green-600 text-sm text-white rounded font-bold hover:bg-green-700 transition"
                >
                  <span className="inline sm:hidden">+</span>
                  <span className="hidden sm:inline">+ Nouveau tournoi</span>
                </Link>
                <Link
                  to={`/locations/${locationId}/edit`}
                  className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-700 text-sm text-white rounded font-bold hover:bg-slate-600 transition"
                  title="Paramètres du lieu"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  <span className="hidden sm:inline">Paramètres</span>
                </Link>
              </>
            )}
          </div>
        </div>
        {/* Mobile location name */}
        <div className="sm:hidden text-center pb-2 px-4">
          <span className="text-base font-semibold italic text-slate-300">
            {location
              ? `${location.name} - ${location.town}`
              : "Chargement ..."}
          </span>
        </div>
      </nav>

      {/* Main content */}
      {authLoading ? (
        // Session is still being restored: don't flash the login prompt
        <div className="flex-1 flex items-center justify-center p-4">
          <div className="h-8 w-8 border-2 border-slate-400 border-t-transparent rounded-full animate-spin"></div>
        </div>
      ) : !user ? (
        <LoginInvite location={location} locationId={locationId} addressPoint={addressPoint} />
      ) : (
        <>
          {/* Mobile: Address + Facebook link */}
          {addressPoint && (
            <div className="lg:hidden px-4 pt-3 flex justify-center">
              <LocationAddress point={addressPoint} />
            </div>
          )}
          {location?.facebook && (
            <div className="lg:hidden px-4 pt-3">
              <a
                href={location.facebook}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 text-sm text-slate-300 hover:text-white transition"
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z" />
                </svg>
                Joignez-nous sur Facebook
              </a>
            </div>
          )}

          {/* Mobile: Podium compact */}
          <div className="lg:hidden px-4 pt-4">
            <PodiumSection
              latestTournamentWithPodium={latestTournamentWithPodium}
              animatePodium={animatePodium}
            />
          </div>

          {/* Mobile: Tab bar */}
          <div className="lg:hidden px-4 pt-4">
            <TournamentTabBar
              tournaments={activeTournaments}
              selectedId={selectedTournamentId}
              onSelect={setSelectedTournamentId}
            />
          </div>

          {/* 3-column desktop layout */}
          <div className="flex-1 flex flex-col lg:flex-row gap-4 p-4">
            {/* Left sidebar — Rankings + Admin */}
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-[60px] max-h-[calc(100vh-76px)] overflow-y-auto no-scrollbar">
                <LeftSidebar
                  location={location}
                  locationId={locationId}
                  locationRankings={locationRankings}
                  isAdmin={isAdmin}
                  locationImage={location?.image}
                  onImageChange={setLocationImage}
                  addressPoint={addressPoint}
                />
              </div>
            </aside>

            {/* Center — Tournament tabs + selected tournament */}
            <main className="flex-1 min-w-0">
              {/* Desktop tab bar */}
              {activeTournaments.length > 1 && (
                <div className="hidden lg:block mb-4">
                  <TournamentTabBar
                    tournaments={activeTournaments}
                    selectedId={selectedTournamentId}
                    onSelect={setSelectedTournamentId}
                  />
                </div>
              )}

              {activeTournaments.length === 0 ? (
                <div className="text-center p-8">
                  <p className="text-xl text-slate-400">
                    Aucun tournoi trouvé pour cet endroit.
                  </p>
                </div>
              ) : selectedTournament ? (
                <div
                  key={selectedTournament.id}
                  id={`tournament-${selectedTournament.id}`}
                  className="w-full"
                >
                  <Tournament
                    tournament={selectedTournament}
                    location={location}
                    user={user}
                    users={users}
                    locationRankings={locationRankings}
                    isAdmin={isAdmin}
                  />
                </div>
              ) : null}
            </main>

            {/* Right sidebar — Podium + Archive */}
            <aside className="hidden lg:block w-80 flex-shrink-0">
              <div className="sticky top-[60px] max-h-[calc(100vh-76px)] overflow-y-auto no-scrollbar">
                <RightSidebar
                  latestTournamentWithPodium={latestTournamentWithPodium}
                  animatePodium={animatePodium}
                  locationId={locationId}
                  isAdmin={isAdmin}
                />
              </div>
            </aside>
          </div>

          {/* Mobile: Collapsible Rankings */}
          {location?.usesRankings && (
            <div className="lg:hidden px-4 pb-2">
              <CollapsibleSection title="Classement" headerRight={isAdmin && (
                <Link
                  to={`/locations/${locationId}/rankings`}
                  className="px-2 py-1 text-xs bg-violet-600 hover:bg-violet-500 text-white rounded font-semibold transition"
                >
                  Gérer
                </Link>
              )}>
                {location.displayRanking && (
                  <PlayerRankings
                    locationRankings={locationRankings}
                    isAdmin={false}
                  />
                )}
              </CollapsibleSection>
            </div>
          )}

          {/* Mobile: Collapsible Archive */}
          <div className="lg:hidden px-4 pb-4">
            <CollapsibleSection title="Archives">
              <TournamentArchive locationId={locationId} isAdmin={isAdmin} />
            </CollapsibleSection>
          </div>
        </>
      )}
    </div>
  );
};

// Remount per location so switching locations never shows the previous one's data
const LocationPage = () => {
  const { locationId } = useParams();
  return <Location key={locationId} />;
};

export default LocationPage;
